import type { ThemeOptions } from '@mui/material/styles';
import type { SemanticConfig } from '../types';
import { darkPrimitives } from '../primitives';
import { buildSemanticPalette } from '../semantic';
import {
    breakpoints,
    getTypographyOptions,
    getComponentOverrides,
    HYPERTRACKER_FONT,
} from '../shared';

const resolved = darkPrimitives;

const semanticConfig: SemanticConfig = {
    neutral: resolved.woodsmoke,
    primary: resolved.riptide,
    success: resolved.shamrock,
    error: resolved.thunderbird,
    warning: resolved.sun,
    accent: resolved.riptide,
};

const semantic = buildSemanticPalette(semanticConfig);

export const hlDarkThemeOptions: ThemeOptions = {
    palette: {
        mode: 'dark',

        // --- Legacy palette (unchanged from original theme.ts) ---
        primary: {
            main: '#247FFF',
            light: '#579DFF',
            dark: '#0B4EAC',
        },
        secondary: {
            main: '#FFAB00',
            dark: '#db8a11',
        },
        background: {
            default: '#1A1B23',
        },
        text: {
            primary: '#fff',
            secondary: '#727687',
        },
        error: {
            main: '#FD6E4E',
            light: '#ff7575',
        },
        warning: {
            main: '#FFAB00',
        },
        info: {
            main: '#FFF',
        },
        success: {
            main: '#38CB89',
            light: '#77d0aa',
        },
        hypertracker: {
            main: '#15C8B1',
            light: '#8CF7E0',
            dark: '#12AA96',
        },
        based: {
            main: '#FF6940',
            light: '#FF7650',
        },
        divider: 'rgba(255, 255, 255, 0.10)',
        redHalfTransparent: 'rgba(253, 110, 78, 0.2)',
        greenHalfTransparent: 'rgba(56, 203, 137, 0.3)',
        blueHalfTransparent: 'rgba(55, 125, 255, 0.3)',
        yellowHalfTransparent: 'rgba(255, 171, 0, 0.3)',
        whiteHalfTransparent: 'rgba(255, 255, 255, 0.2)',
        whiteTransparent: 'rgba(255, 255, 255, 0.10)',
        hyperLiquidColor: '#15C8B1',
        hyperLiquidColor2: '#8CF7E0',
        hyperLiquidColor3: '#9BE5C4',
        blueTelegram: '#29ABEE',
        backgrounds: {
            bg0: '#000000',
            bg1: '#1A1B23',
            bg2: '#1D1E25',
            bg3: '#26272C',
            bg4: '#3B3C46',
            bg5: '#262731',
            bg6: '#F5F7FB',
            bg7: '#212121',
            bg8: '#14141B',
            buttonSelectBg: 'rgba(140, 247, 224, 0.19)',
            sideBarBackground: '#0E0E0E',
            layoutHeaderBg: '#111117',
            pageHeaderBackground: '#1A1B23',
            progressDefaultBg: '#3B3C46',
            loginFormBg: '#26272C',
            loginInputsBg: '#3B3C46',
            grayButtonBg: '#43485C',
            turquoiseBg: '#8CF7E0',
            greenLightningBg: '#15C8B1',
            bullMeterVeryBullishBg: '#1F3D37',
            bullMeterBullishBg: '#2F5950',
            bullMeterSlightlyBullishBg: '#386C62',
            bullMeterVeryBearishBg: '#5E3430',
            bullMeterBearishBg: '#7A423D',
            bullMeterSlightlyBearishBg: '#884D47',
            htBgGradient: 'linear-gradient(180deg, #0C0C10 0%, #1A1B23 100%)',
            heatmapTableBg: '#0C0D10',
            walletMainBg: '#0D0D0D',
            blurredTooltipBg: 'rgba(59, 60, 70, 0.2)',
            createAlertBg: '#161619',
            purchasePageBg: '#0B0C0E',
            appSwitcherBg: '#14161A',
        },
        customColors: {
            halfTransparentHL: 'rgba(140, 247, 224, 0.5)',
            bronze: '#CD5C00',
            customYellow: '#FDBC18',
        },
        customText: {
            text1: '#707077',
            text2: '#43485C',
            text3: '#B0B7C3',
            text4: '#FFF',
            text5: '#1a1b23',
            text6: '#001C42',
            text7: '#A0A2A4',
            text8: '#505762',
            text9: '#F0F0F0',
            drawerTextColor: '#777E90',
        },

        // --- New design-system layer (Figma primitives + semantic) ---
        primitives: resolved,
        semantic,
    },
    typography: getTypographyOptions(HYPERTRACKER_FONT),
    breakpoints,
    components: getComponentOverrides(true),
};
